// /home/szymon/kreator2/frontend/src/components/editor/hooks/useFakeRebusDragDrop.js
import { useState, useEffect, useCallback, useRef } from 'react';
import getSvgDimensions from '../../../utils/getSvgDimensions'; // <--- IMPORT

/**
 * Hook do zarządzania logiką "Fake Drag-and-Drop" dla rebusów.
 *
 * @param {function} onDrop - Callback wywoływany, gdy rebus zostanie upuszczony na canvas.
 *                            Oczekuje obiektu z { file, width, height, x, y }.
 * @returns {object} Zwraca obiekt zawierający:
 *  - fakeDraggingRebus: Stan przechowujący dane przeciąganego rebusu (obiekt File) lub null.
 *  - setFakeDraggingRebus: Funkcja do ustawiania stanu (potrzebna do czyszczenia).
 *  - handleFakeRebusMouseDown: Funkcja do wywołania w komponencie panelu bocznego przy rozpoczęciu przeciągania.
 *  - handleCanvasInsideChange: Funkcja do wywołania w komponencie Canvas, gdy kursor wchodzi/wychodzi z obszaru canvas.
 *  - handleCanvasPosChange: Funkcja do wywołania w komponencie Canvas przy zmianie pozycji kursora nad canvasem.
 */
export function useFakeRebusDragDrop({ onDrop }) {
  const [fakeDraggingRebus, setFakeDraggingRebus] = useState(null);
  const [canvasInside, setCanvasInside] = useState(false);
  const lastCanvasPosRef = useRef({ x: 0, y: 0 });

  // Callback wywoływany przez EditorCanvas, gdy kursor wchodzi/wychodzi
  const handleCanvasInsideChange = useCallback((val) => {
    setCanvasInside(val);
  }, []);

  // Callback wywoływany przez EditorCanvas przy ruchu kursora
  const handleCanvasPosChange = useCallback((pos) => {
    lastCanvasPosRef.current = pos;
  }, []);

  // Callback wywoływany przez EditorSidePanel przy kliknięciu na rebus
  // Zmieniono na async, aby pobrać zawartość SVG
  const handleFakeRebusMouseDown = useCallback(async (rebData) => {
    console.log("[useFakeRebusDragDrop] handleFakeRebusMouseDown => fetching SVG content for:", rebData);
    // Obsługujemy zarówno url jak i svgUrl dla kompatybilności
    const url = rebData.svgUrl || rebData.url;
    if (!rebData || !url) {
      console.error("[useFakeRebusDragDrop] Invalid rebData provided to handleFakeRebusMouseDown - missing url/svgUrl");
      return;
    }

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch SVG: ${response.status} ${response.statusText}`);
      }
      const svgText = await response.text();
      // Tworzymy obiekt File z pobranego SVG
      const svgFile = new File([svgText], `${rebData.id || 'rebus'}.svg`, { type: 'image/svg+xml' });

      let actualWidth = rebData.width; // Domyślnie z rebData
      let actualHeight = rebData.height; // Domyślnie z rebData

      try {
        // Przekazujemy null jako pierwszy argument (svgUrl), a svgText jako drugi.
        const dimensions = await getSvgDimensions(null, svgText); 
        if (dimensions && typeof dimensions.width === 'number' && typeof dimensions.height === 'number' && dimensions.width > 0 && dimensions.height > 0) {
          actualWidth = dimensions.width;
          actualHeight = dimensions.height;
          console.log("[useFakeRebusDragDrop] Actual SVG dimensions from getSvgDimensions (using svgText):", dimensions);
        } else {
          console.warn("[useFakeRebusDragDrop] getSvgDimensions did not return valid dimensions, using rebData dimensions. Received:", dimensions);
        }
      } catch (dimError) {
        console.error("[useFakeRebusDragDrop] Error calling getSvgDimensions with svgText, using rebData dimensions:", dimError);
      }

      console.log("[useFakeRebusDragDrop] SVG fetched and File object created. Storing fake dnd data:", { file: svgFile, width: actualWidth, height: actualHeight });
      // Przechowujemy obiekt File zamiast URL i używamy odczytanych wymiarów
      setFakeDraggingRebus({
        file: svgFile,
        width: actualWidth,
        height: actualHeight
      });
    } catch (error) {
      console.error("[useFakeRebusDragDrop] Error fetching or creating SVG file:", error);
      setFakeDraggingRebus(null); // Reset w razie błędu
    }
  }, []); // Zależności pozostają puste, bo funkcja nie zależy od stanu hooka

  // GLOBAL mouseUp + obsługa kursora podczas fake drag
  useEffect(() => {
    // Ustawienie kursora przy rozpoczęciu przeciągania
    if (fakeDraggingRebus) {
      document.body.style.cursor = 'grabbing';
    } else {
      // Reset kursora, jeśli stan się zmienił na null (np. anulowanie)
      document.body.style.cursor = 'default';
    }

    function globalMouseUp(ev) {
      if (!fakeDraggingRebus) return; // nic nie było przenoszone

      // Zawsze resetujemy kursor po mouseup
      document.body.style.cursor = 'default';

      // Sprawdzamy, czy upuszczenie nastąpiło nad canvasem i czy mamy callback onDrop
      if (canvasInside && typeof onDrop === 'function') {
        console.log("[useFakeRebusDragDrop] globalMouseUp => Dropping rebus on canvas at:", lastCanvasPosRef.current);
        // Wywołujemy callback onDrop z danymi rebusu i pozycją kursora
        onDrop({
          file: fakeDraggingRebus.file,
          width: fakeDraggingRebus.width,
          height: fakeDraggingRebus.height,
          x: lastCanvasPosRef.current.x,
          y: lastCanvasPosRef.current.y
        });
      } else {
        console.log("[useFakeRebusDragDrop] globalMouseUp => Not over canvas or no onDrop handler, cancelling.");
      }
      
      // Czyścimy stan po puszczeniu myszy
      setFakeDraggingRebus(null);
    }

    window.addEventListener("mouseup", globalMouseUp);
    return () => {
      window.removeEventListener("mouseup", globalMouseUp);
      // Upewnij się, że kursor jest resetowany, jeśli komponent zostanie odmontowany podczas przeciągania
      if (fakeDraggingRebus) {
        document.body.style.cursor = 'default';
      }
    };
    // Usunięto onDrop z zależności
  }, [fakeDraggingRebus, canvasInside, onDrop]); // Dodano onDrop do zależności

  return {
    fakeDraggingRebus, // Stan nadal potrzebny dla konsumenta
    setFakeDraggingRebus, // Eksportujemy setter, aby Editor mógł go wyczyścić w razie potrzeby
    handleFakeRebusMouseDown,
    handleCanvasInsideChange,
    handleCanvasPosChange
  };
}
